// ============================================================================
// UrlhausSyncPanel.js
// Drop this file into src/components/
//
// Usage in Iocview.js — add near the top stats area:
//   import UrlhausSyncPanel from './UrlhausSyncPanel';
//   ...
//   <UrlhausSyncPanel />
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  RefreshCw, CheckCircle, XCircle, Clock,
  AlertTriangle, Database, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';

const UrlhausSyncPanel = ({ activeCompany }) => {
  const { getToken } = useAuth();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // ── Fetch sync status ───────────────────────────────────────────────────────

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/urlhaus/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStatus(data);
      setLastRefresh(new Date());

      // If a sync is running, keep polling
      if (data.last_sync?.status === 'running') {
        setTimeout(() => fetchStatus(true), 4000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 60s
    const interval = setInterval(() => fetchStatus(true), 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // ── Trigger manual sync ─────────────────────────────────────────────────────

  const triggerSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/urlhaus/sync', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      // Start polling
      setTimeout(() => fetchStatus(true), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const formatTime = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  const getStatusIcon = (s) => {
    if (s === 'success') return <CheckCircle className="w-3.5 h-3.5 text-green-400" />;
    if (s === 'running') return <RefreshCw className="w-3.5 h-3.5 text-blue-400 animate-spin" />;
    if (s === 'error')   return <XCircle className="w-3.5 h-3.5 text-red-400" />;
    return <Clock className="w-3.5 h-3.5 text-slate-400" />;
  };

  const getStatusColor = (s) => {
    if (s === 'success') return 'text-green-400';
    if (s === 'running') return 'text-blue-400';
    if (s === 'error')   return 'text-red-400';
    return 'text-slate-400';
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-3 flex items-center gap-2 text-slate-400 text-xs">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        <span>Loading URLhaus sync status...</span>
      </div>
    );
  }

  const lastSync = status?.last_sync;
  const isRunning = lastSync?.status === 'running';

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
      {/* Header row */}
      <div className="px-3 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {/* abuse.ch badge */}
          <div className="flex items-center gap-1.5 bg-orange-500/15 border border-orange-500/30 rounded px-2 py-0.5">
            <Database className="w-3 h-3 text-orange-400" />
            <span className="text-orange-300 text-xs font-semibold">abuse.ch</span>
          </div>

          <span className="text-slate-300 text-xs font-medium">URLhaus</span>

          {/* IOC count badge */}
          {status?.urlhaus_ioc_count > 0 && (
            <span className="bg-slate-700/80 text-slate-300 text-xs px-2 py-0.5 rounded-full font-mono">
              {status.urlhaus_ioc_count.toLocaleString()} IOCs
            </span>
          )}

          {/* +24h badge */}
          {status?.recent_24h > 0 && (
            <span className="bg-green-500/15 text-green-400 text-xs px-1.5 py-0.5 rounded-full">
              +{status.recent_24h} today
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Last sync status */}
          {lastSync && (
            <div className={`flex items-center gap-1 text-xs ${getStatusColor(lastSync.status)}`}>
              {getStatusIcon(lastSync.status)}
              <span>{isRunning ? 'Syncing...' : formatTime(lastSync.finished_at || lastSync.started_at)}</span>
            </div>
          )}

          {!lastSync && !status?.synced && (
            <span className="text-xs text-slate-500">Never synced</span>
          )}

          {/* Sync button */}
          <button
            onClick={triggerSync}
            disabled={syncing || isRunning}
            className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-500/20 hover:bg-orange-500/30 
                       border border-orange-500/30 hover:border-orange-500/50
                       text-orange-300 text-xs rounded-md transition-all
                       disabled:opacity-50 disabled:cursor-not-allowed"
            title="Sync now from URLhaus"
          >
            <RefreshCw className={`w-3 h-3 ${(syncing || isRunning) ? 'animate-spin' : ''}`} />
            <span>{syncing || isRunning ? 'Syncing' : 'Sync Now'}</span>
          </button>

          {/* Expand toggle (only if there's history) */}
          {status?.sync_history?.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-slate-500 hover:text-slate-300 transition-colors p-0.5"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-3 pb-2.5 flex items-center gap-1.5 text-red-400 text-xs">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Expanded: sync history */}
      {expanded && status?.sync_history?.length > 0 && (
        <div className="border-t border-slate-700/50">
          <div className="px-3 py-1.5 text-xs text-slate-500 font-medium uppercase tracking-wide">
            Recent Syncs
          </div>
          <div className="divide-y divide-slate-700/30">
            {status.sync_history.map((run) => (
              <div key={run.id} className="px-3 py-2 flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-1.5">
                  {getStatusIcon(run.status)}
                  <span className={getStatusColor(run.status)}>
                    {run.status}
                  </span>
                  {run.error_message && (
                    <span className="text-red-400/70 truncate max-w-[180px]" title={run.error_message}>
                      — {run.error_message.slice(0, 50)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-slate-500 flex-shrink-0">
                  {run.records_inserted > 0 && (
                    <span className="text-green-400/70">+{run.records_inserted}</span>
                  )}
                  {run.records_updated > 0 && (
                    <span className="text-blue-400/70">~{run.records_updated}</span>
                  )}
                  <span>{formatTime(run.finished_at || run.started_at)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Link to URLhaus */}
          <div className="px-3 py-2 border-t border-slate-700/30">
            <a
              href="https://urlhaus.abuse.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-orange-400/70 hover:text-orange-400 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              <span>urlhaus.abuse.ch</span>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default UrlhausSyncPanel;